import React, { useEffect, useMemo, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { fetchCouponsAdmin, createCouponAdmin, updateCouponAdmin, deleteCouponAdmin } from '../../services/couponService';
import { formatDate } from '../../utils/dateFormatter';
import { displayCurrency, formatCurrency } from '../../utils/currencyUtils';
import { useSettings } from '../../context/SettingsContext';
import ConfirmDialog from '../ui/ConfirmDialog';
import NotificationSnackbar from '../ui/NotificationSnackbar';
import { CrmBadge, CrmButton, CrmInput, CrmModal, CrmSelect, CrmTooltip } from '../ui';
import { CrmEntityListPage } from '../listing';

const emptyForm = {
  code: '', type: 'flat', value: 0, min_order_amount: 0, starts_at: '', ends_at: '',
  usage_limit: '', usage_limit_per_user: '', active: 1,
};

export default function CouponList() {
  const { settings } = useSettings();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIds, setDeleteIds] = useState([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await fetchCouponsAdmin();
      setCoupons(Array.isArray(rows) ? rows : []);
    } catch {
      setNotification({ open: true, message: 'Failed to load coupons', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (coupon) => {
    setEditingId(coupon.id);
    setForm({
      code: coupon.code || '', type: coupon.type || 'flat', value: coupon.value || 0,
      min_order_amount: coupon.min_order_amount || 0,
      starts_at: coupon.starts_at ? String(coupon.starts_at).split('T')[0] : '',
      ends_at: coupon.ends_at ? String(coupon.ends_at).split('T')[0] : '',
      usage_limit: coupon.usage_limit || '', usage_limit_per_user: coupon.usage_limit_per_user || '',
      active: coupon.active ? 1 : 0,
    });
    setShowForm(true);
  };

  const saveCoupon = async () => {
    if (!form.code.trim()) {
      setNotification({ open: true, message: 'Enter coupon code', severity: 'warning' });
      return;
    }
    const payload = {
      code: form.code.trim(), type: form.type, value: Number(form.value || 0),
      min_order_amount: Number(form.min_order_amount || 0), starts_at: form.starts_at || null,
      ends_at: form.ends_at || null, usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
      usage_limit_per_user: form.usage_limit_per_user ? Number(form.usage_limit_per_user) : null,
      active: form.active ? 1 : 0,
    };
    try {
      if (editingId) await updateCouponAdmin(editingId, payload);
      else await createCouponAdmin(payload);
      setNotification({ open: true, message: editingId ? 'Coupon updated' : 'Coupon created', severity: 'success' });
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      await load();
    } catch {
      setNotification({ open: true, message: editingId ? 'Failed to update coupon' : 'Failed to create coupon', severity: 'error' });
    }
  };

  const deleteCoupons = async () => {
    try {
      for (const id of deleteIds) await deleteCouponAdmin(id);
      setNotification({ open: true, message: `${deleteIds.length} coupon${deleteIds.length === 1 ? '' : 's'} deleted`, severity: 'success' });
      await load();
    } catch {
      setNotification({ open: true, message: 'Failed to delete coupon', severity: 'error' });
    } finally {
      setConfirmOpen(false);
      setDeleteIds([]);
    }
  };

  const columns = useMemo(() => [
    {
      id: 'code', label: 'Code', width: 180, minWidth: 140, maxWidth: 280,
      hideable: false, defaultPinned: true, pinnable: true, stopRowClick: true,
      renderCell: (coupon) => <button type="button" className="crm-table-primary-link" onClick={() => openEdit(coupon)}>{coupon.code || '—'}</button>,
    },
    { id: 'type', label: 'Type', width: 120, minWidth: 100, maxWidth: 180, filterable: true },
    {
      id: 'value', label: 'Value', width: 150, minWidth: 120, maxWidth: 220, type: 'number',
      accessor: (coupon) => Number(coupon.value || 0),
      renderCell: (coupon) => coupon.type === 'percent' ? `${Math.round(Number(coupon.value || 0))}%` : formatCurrency(coupon.value, settings?.currency_code),
    },
    {
      id: 'min_order_amount', label: 'Minimum order', width: 170, minWidth: 140, maxWidth: 240, type: 'currency',
      accessor: (coupon) => Number(coupon.min_order_amount || 0),
      renderCell: (coupon) => formatCurrency(coupon.min_order_amount, settings?.currency_code),
    },
    { id: 'starts_at', label: 'Starts', width: 150, minWidth: 130, maxWidth: 220, type: 'date', filterable: true, renderCell: (coupon) => formatDate(coupon.starts_at) || '—' },
    { id: 'ends_at', label: 'Ends', width: 150, minWidth: 130, maxWidth: 220, type: 'date', filterable: true, renderCell: (coupon) => formatDate(coupon.ends_at) || '—' },
    {
      id: 'usage', label: 'Usage', width: 130, minWidth: 110, maxWidth: 190, sortable: false,
      accessor: (coupon) => Number(coupon.times_used || 0),
      renderCell: (coupon) => `${Number(coupon.times_used || 0)}${coupon.usage_limit ? ` / ${coupon.usage_limit}` : ''}`,
    },
    {
      id: 'active', label: 'Status', width: 120, minWidth: 100, maxWidth: 170, type: 'boolean', filterable: true,
      accessor: (coupon) => Boolean(coupon.active),
      renderCell: (coupon) => <CrmBadge tone={coupon.active ? 'success' : 'neutral'}>{coupon.active ? 'Active' : 'Inactive'}</CrmBadge>,
    },
    {
      id: 'row_actions', label: 'Actions', width: 104, minWidth: 104, maxWidth: 132,
      hideable: false, sortable: false, searchable: false, className: 'crm-data-table__actions-cell',
      renderCell: (coupon) => (
        <span className="crm-row-actions">
          <CrmTooltip content="Edit coupon"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => openEdit(coupon)} aria-label="Edit coupon"><EditOutlinedIcon /></CrmButton></CrmTooltip>
          <CrmTooltip content="Delete coupon"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action crm-row-action--danger" onClick={() => { setDeleteIds([coupon.id]); setConfirmOpen(true); }} aria-label="Delete coupon"><DeleteOutlineIcon /></CrmButton></CrmTooltip>
        </span>
      ),
    },
  ], [settings?.currency_code]);

  const filters = useMemo(() => [
    { id: 'type', columnId: 'type', label: 'Type', type: 'select', options: [{ value: 'flat', label: 'Flat' }, { value: 'percent', label: 'Percent' }], quick: true },
    { id: 'active', columnId: 'active', label: 'Status', type: 'select', options: [{ value: true, label: 'Active' }, { value: false, label: 'Inactive' }], quick: true },
    { id: 'starts', columnId: 'starts_at', label: 'Start date', type: 'date-range', quick: true },
    { id: 'ends', columnId: 'ends_at', label: 'End date', type: 'date-range' },
  ], []);

  const updateForm = (field) => (event) => setForm((previous) => ({ ...previous, [field]: event.target.value }));
  const currencySymbol = displayCurrency(settings?.currency_code);

  return (
    <div className="contacts-page">
      <CrmEntityListPage
        moduleKey="coupons"
        title="Coupons"
        itemLabel="coupon"
        items={coupons}
        columns={columns}
        filterDefinitions={filters}
        initialSort={{ columnId: 'starts_at', direction: 'desc' }}
        loading={loading}
        onRefresh={load}
        onCreate={openCreate}
        createLabel="New coupon"
        onRowClick={openEdit}
        onDeleteSelected={(ids) => { setDeleteIds(ids); setConfirmOpen(true); }}
      />

      <CrmModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Edit coupon' : 'Create coupon'}
        size="md"
        footer={<><CrmButton onClick={() => setShowForm(false)}>Cancel</CrmButton><CrmButton variant="primary" onClick={saveCoupon}>Save coupon</CrmButton></>}
      >
        <div className="crm-form-grid">
          <CrmInput label="Coupon code" value={form.code} onChange={(event) => setForm((previous) => ({ ...previous, code: event.target.value.toUpperCase() }))} required />
          <CrmSelect label="Type" value={form.type} onChange={updateForm('type')}><option value="flat">Flat</option><option value="percent">Percent</option></CrmSelect>
          <CrmInput label={form.type === 'percent' ? 'Percentage (%)' : `Value (${currencySymbol})`} type="number" step="1" value={form.value} onChange={updateForm('value')} />
          <CrmInput label={`Minimum order (${currencySymbol})`} type="number" step="1" value={form.min_order_amount} onChange={updateForm('min_order_amount')} />
          <CrmInput label="Starts at" type="date" value={form.starts_at} onChange={updateForm('starts_at')} />
          <CrmInput label="Ends at" type="date" value={form.ends_at} onChange={updateForm('ends_at')} />
          <CrmInput label="Usage limit" type="number" step="1" value={form.usage_limit} onChange={updateForm('usage_limit')} />
          <CrmInput label="Usage limit per user" type="number" step="1" value={form.usage_limit_per_user} onChange={updateForm('usage_limit_per_user')} />
          <CrmSelect label="Status" value={form.active} onChange={(event) => setForm((previous) => ({ ...previous, active: Number(event.target.value) }))}><option value={1}>Active</option><option value={0}>Inactive</option></CrmSelect>
        </div>
      </CrmModal>

      <ConfirmDialog open={confirmOpen} title="Delete coupon?" message={`Delete ${deleteIds.length} coupon${deleteIds.length === 1 ? '' : 's'}? This cannot be undone.`} confirmText="Delete" onConfirm={deleteCoupons} onCancel={() => setConfirmOpen(false)} />
      <NotificationSnackbar {...notification} onClose={() => setNotification((previous) => ({ ...previous, open: false }))} />
    </div>
  );
}
